import { stepLabel } from 'src/constants/collect-data/stepTypeLabels'
import type {
  CollectDataTemplateStepRow,
  OnboardingTemplate,
} from 'src/interfaces/onboardingTemplate'

/**
 * Flattens a template's steps into display order: configs by `order`, then steps by `order`
 * within each config.
 *
 * Steps are reachable ONLY through `collectDataTemplateStepsConfigs` — the backend's
 * `getFullByPartnerId` joins that way — so a template whose steps predate the configs table
 * legitimately flattens to nothing. See the design doc's §2.2.
 *
 * Sorts copies: the arrays come from Redux state and must never be reordered in place.
 */
export const getTemplateSteps = (template: OnboardingTemplate): CollectDataTemplateStepRow[] => {
  const configs = template.collectDataTemplateStepsConfigs ?? []

  return [...configs]
    .sort((leftConfig, rightConfig) => {
      return leftConfig.order - rightConfig.order
    })
    .flatMap((config) => {
      const steps = config.collectDataTemplateSteps ?? []

      return [...steps].sort((leftStep, rightStep) => {
        return leftStep.order - rightStep.order
      })
    })
}

/** Total steps across every config. 0 is a legitimate answer — see `getTemplateSteps`. */
export const countTemplateSteps = (template: OnboardingTemplate): number => {
  const configs = template.collectDataTemplateStepsConfigs ?? []

  return configs.reduce((total, config) => {
    return total + (config.collectDataTemplateSteps?.length ?? 0)
  }, 0)
}

/**
 * In-memory search over the templates list — the endpoint takes no search parameter. Matches
 * the template name, a step's display label, or a step's raw type, so both "insurance" and a
 * `insurance_coverage` string pasted from the API find the row.
 *
 * Returns the input array by reference for an empty query so an untouched search box does not
 * churn the caller's `useMemo`.
 */
export const filterOnboardingTemplates = (
  templates: OnboardingTemplate[],
  search: string,
): OnboardingTemplate[] => {
  const normalizedSearch = search.trim().toLowerCase()

  if (normalizedSearch === '') {
    return templates
  }

  return templates.filter((template) => {
    if (template.name.toLowerCase().includes(normalizedSearch)) {
      return true
    }

    return getTemplateSteps(template).some((step) => {
      const haystack = [stepLabel(step.type), step.type]

      return haystack.some((value) => {
        return value.toLowerCase().includes(normalizedSearch)
      })
    })
  })
}
